/* ──────────────────────────────────────────────────────────────
   src/pages/reports/clientes/CancelacionAlumnoPage.tsx
   ────────────────────────────────────────────────────────────── */
import React, { useState } from "react";
import dayjs from "dayjs";
import {
  Stack,
  Paper,
  Typography,
  Button,
  TextField,
  LinearProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useTranslation } from "react-i18next";
import { useCancelacionAlumnoReport } from "../../../hooks/useReportJob";
import { getReservasCancelacion, ReservaIncidencia as ReservaCan } from "../../../api/reversas";
import ReservationFormDialog from "../../../components/ReservationFormDialog";
import { downloadReportFile } from "../../../components/utils/downloadXlsx";

/* fila resumen que devuelve el worker */
export interface CancelAluRow {
  alumnoId: number;
  alumno: string;
  reservas: number;
  cancelTot: number;
  ratioTot: number; // 0-1
  cancelAlu: number;
  ratioAlu: number; // 0-1
  ultimaCanc: string; // YYYY-MM-DD  | '—'
}

export default function CancelacionAlumnoPage() {
  const { t } = useTranslation<"translation">();
  /* rango fechas */
  const [desde, setDesde] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [hasta, setHasta] = useState(dayjs().format("YYYY-MM-DD"));

  /* cola + polling */
  const { run, useStatus } = useCancelacionAlumnoReport();
  const [jobId, setJobId] = useState<string>();
  const { data: status } = useStatus(jobId);

  /* detalle cancelaciones */
  const [openRow, setOpenRow] = useState<number>();
  const [detalles, setDetalles] = useState<Record<number, ReservaCan[]>>({});

  /* diálogo reserva */
  const [selId, setSelId] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [selData, setSelData] = useState<any>();

  const generar = async () => {
    const { jobId } = await run({ desde, hasta });
    setJobId(jobId);
    setOpenRow(undefined);
    setDetalles({});
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      try {
        await downloadReportFile(status.result.url);
      } catch (error) {
        console.error("Error al descargar:", error);
      }
    }
  };

  const toggleDetalle = async (aluId: number) => {
    if (openRow === aluId) {
      setOpenRow(undefined);
      return;
    }
    if (!detalles[aluId]) {
      const rows = await getReservasCancelacion(aluId, desde, hasta);
      setDetalles((prev) => ({ ...prev, [aluId]: rows }));
    }
    setOpenRow(aluId);
  };

  const abrirReserva = async (id: number) => {
    setSelId(id);
    setLoading(true);
    const { getReserva } = await import("../../../api/reversas");
    getReserva(id)
      .then((r) => setSelData(r))
      .finally(() => setLoading(false));
  };

  /* ─────────────────────────── UI ─────────────────────────── */
  return (
    <Stack spacing={3}>
      <Typography variant="h5">
        {t("cancelacionAlumno.titulo", "Ratio de cancelación por alumno")}
      </Typography>

      {/* filtros */}
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          type="date"
          label={t("cancelacionAlumno.filtros.desde")}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />
        <TextField
          type="date"
          label={t("cancelacionAlumno.filtros.hasta")}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
        />
        <Button variant="contained" onClick={generar}>
          {t("cancelacionAlumno.filtros.generar")}
        </Button>
      </Stack>

      {/* progreso */}
      {status && !status.done && (
        <Stack spacing={1}>
          <LinearProgress variant="determinate" value={status.progress} />
          <Typography variant="body2">
            {t("cancelacionAlumno.estado.generando", {
              progress: status.progress,
            })}
          </Typography>
        </Stack>
      )}

      {/* resultado */}
      {status?.done && status.result && (
        <>
          <Alert severity="success">
            {t("cancelacionAlumno.estado.exito")}{" "}
            <Button
              variant="text"
              size="small"
              onClick={handleDownload}
              sx={{ textTransform: "none", textDecoration: "underline", p: 0, minWidth: "auto" }}
            >
              {t("cancelacionAlumno.estado.descargar")}
            </Button>{" "}
            (
            {t("cancelacionAlumno.estado.totalAlumnos", {
              count: status.result.registros,
            })}
            )
          </Alert>

          <Paper sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>
                    <b>{t("cancelacionAlumno.tabla.alumno")}</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{t("cancelacionAlumno.tabla.reservas")}</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{t("cancelacionAlumno.tabla.cancelTot")}.</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{t("cancelacionAlumno.tabla.ratioTot")}</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{t("cancelacionAlumno.tabla.cancelAlu")}</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{t("cancelacionAlumno.tabla.ratioAlu")}</b>
                  </TableCell>
                  <TableCell>
                    <b>{t("cancelacionAlumno.tabla.ultima")}</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {(status.result.rows as CancelAluRow[])
                  .sort((a, b) => b.cancelTot - a.cancelTot)
                  .map((r) => (
                    <React.Fragment key={r.alumnoId}>
                      {/* resumen */}
                      <TableRow hover>
                        <TableCell>
                          <IconButton size="small" onClick={() => toggleDetalle(r.alumnoId)}>
                            {openRow === r.alumnoId ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{r.alumno}</TableCell>
                        <TableCell align="right">{r.reservas}</TableCell>
                        <TableCell align="right">{r.cancelTot}</TableCell>
                        <TableCell align="right">{(r.ratioTot * 100).toFixed(1)}%</TableCell>
                        <TableCell align="right">{r.cancelAlu}</TableCell>
                        <TableCell align="right">{(r.ratioAlu * 100).toFixed(1)}%</TableCell>
                        <TableCell>{r.ultimaCanc}</TableCell>
                      </TableRow>

                      {/* detalle reservas */}
                      <TableRow>
                        <TableCell style={{ padding: 0 }} colSpan={8}>
                          <Collapse in={openRow === r.alumnoId} unmountOnExit>
                            <Paper variant="outlined" sx={{ m: 1 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>{t("cancelacionAlumno.detalle.fecha")}</TableCell>
                                    <TableCell>{t("cancelacionAlumno.detalle.profesor")}</TableCell>
                                    <TableCell>{t("cancelacionAlumno.detalle.estado")}</TableCell>
                                    <TableCell align="center">
                                      {t("cancelacionAlumno.detalle.ver")}
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(detalles[r.alumnoId] ?? []).map((d) => (
                                    <TableRow key={d.id}>
                                      <TableCell>{dayjs(d.fecha).format("DD-MM-YY")}</TableCell>
                                      <TableCell>{d.cliente}</TableCell>
                                      <TableCell>{d.estado}</TableCell>
                                      <TableCell align="center">
                                        <IconButton size="small" onClick={() => abrirReserva(d.id)}>
                                          <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Paper>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {/* diálogo reserva */}
      <ReservationFormDialog
        open={!!selId}
        mode="edit"
        readOnly
        reservaId={selId}
        initialValues={selData}
        onClose={() => setSelId(undefined)}
      >
        {loading && (
          <Stack alignItems="center" py={4}>
            <LinearProgress sx={{ width: 200 }} />
          </Stack>
        )}
      </ReservationFormDialog>
    </Stack>
  );
}
